import * as THREE from "three";
import { Tank } from "./Tank";
import { TANK_CONFIG, ENVIRONMENT_CONFIG } from "./config";
import { showWastedOverlay } from "./hud";
import { createExplosion } from "./effects/Explosion";
import { createLaserExplosion } from "./effects/LaserExplosion";
import { createLightningExplosion } from "./effects/LightningExplosion";
import { TrainingTrackService } from "./training/TrainingTrackService";
import { TrainingTrackManager } from "./training/ModularTrainingTrackManager";
import { TrainingTrackSetup } from "./training/TrainingTrackSetup";
import { BotManager } from "./BotManager";
import { Bot } from "./Bot";
import { TankModelConfig, TANK_MODELS } from "./tanks/TankModel";

// Sprawdza kolizję między dwoma czołgami
export const checkTankCollision = (tank1: Tank, tank2: Tank): boolean => {
  const distance = tank1.mesh.position.distanceTo(tank2.mesh.position);
  const minDistance = ENVIRONMENT_CONFIG.COLLISION.TANK_RADIUS * 2;

  // DEBUG: Log sprawdzania kolizji
  console.log("Tank collision check:", {
    tank1Id: tank1.playerId,
    tank2Id: tank2.playerId,
    distance,
    collisionRadius: ENVIRONMENT_CONFIG.COLLISION.TANK_RADIUS,
  });

  return distance < minDistance;
};

// Oblicza wektor odbicia dla czołgu
const calculateBounceDirection = (tank1: Tank, tank2: Tank): THREE.Vector3 => {
  const direction = new THREE.Vector3().subVectors(tank1.mesh.position, tank2.mesh.position).normalize();

  // DEBUG: Log wektora odbicia
  console.log("Bounce direction calculated:", {
    tank1Id: tank1.playerId,
    tank2Id: tank2.playerId,
    bounceDirection: direction.toArray(),
  });

  return direction;
};

// Track active smoke effects
const activeSmokeEffects = new Map<string, THREE.Points>();

// Efekt dymu przy uszkodzeniu
const createDamageSmoke = (tank: Tank) => {
  try {
    console.log("[Damage] Starting smoke effect creation:", {
      tankId: tank.playerId,
      tankMeshExists: !!tank.mesh,
      tankHealth: tank.health,
      healthPercentage: (tank.health / tank.maxHealth) * 100,
      currentSmoke: tank.damageSmoke,
    });

    if (!tank.mesh) {
      throw new Error("[Damage] Tank mesh is not available for smoke effect");
    }

    // Remove existing smoke effect if present
    removeDamageSmoke(tank);

    const smokeGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(100 * 3); // 100 particles
    for (let i = 0; i < 100; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2;
      positions[i * 3 + 1] = Math.random() * 1 + 0.2;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2;
    }
    smokeGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const smokeMaterial = new THREE.PointsMaterial({
      color: 0x555555,
      size: 0.2,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });

    const smoke = new THREE.Points(smokeGeometry, smokeMaterial);
    tank.mesh.add(smoke);
    tank.damageSmoke = true;

    // Store the smoke effect reference
    activeSmokeEffects.set(tank.playerId, smoke);

    console.log("[Damage] Smoke effect created successfully:", {
      tankId: tank.playerId,
      smokeAdded: true,
      smokeInScene: !!smoke.parent,
      damageSmokeFlagSet: tank.damageSmoke,
    });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("[Damage] Error creating smoke effect:", {
      tankId: tank.playerId,
      error: errorMessage,
      tankState: {
        hasMesh: !!tank.mesh,
        health: tank.health,
        isDestroyed: tank.isDestroyed,
      },
    });
  }
};

// Function to remove smoke effect and cleanup resources
export const removeDamageSmoke = (tank: Tank) => {
  const existingSmoke = activeSmokeEffects.get(tank.playerId);
  if (existingSmoke) {
    // Remove from scene
    tank.mesh.remove(existingSmoke);

    // Dispose of geometry and material
    if (existingSmoke.geometry) {
      existingSmoke.geometry.dispose();
    }
    if (existingSmoke.material) {
      (existingSmoke.material as THREE.Material).dispose();
    }

    // Remove from tracking
    activeSmokeEffects.delete(tank.playerId);
    tank.damageSmoke = false;

    console.log("[Damage] Removed smoke effect for tank:", tank.playerId);
  }
};

// Track last collision time between tanks
const lastCollisionTimes = new Map<string, number>();
const COLLISION_CLEANUP_INTERVAL = 60000; // Cleanup every 60 seconds
const COLLISION_DATA_TTL = 300000; // Remove collision data older than 5 minutes

// Function to clean up old collision data
const cleanupCollisionData = () => {
  const currentTime = Date.now();
  for (const [key, timestamp] of lastCollisionTimes.entries()) {
    if (currentTime - timestamp > COLLISION_DATA_TTL) {
      lastCollisionTimes.delete(key);
    }
  }
};

// Set up periodic cleanup
setInterval(cleanupCollisionData, COLLISION_CLEANUP_INTERVAL);

export const handleTankCollision = (tank1: Tank, tank2: Tank): void => {
  // Create a unique key for this tank pair
  const collisionKey = [tank1.playerId, tank2.playerId].sort().join("-");
  const currentTime = Date.now();
  const COLLISION_COOLDOWN = 1000; // Increased cooldown to 1 second

  // Check if tanks are still in cooldown from previous collision
  if (lastCollisionTimes.has(collisionKey)) {
    const timeSinceLastCollision = currentTime - lastCollisionTimes.get(collisionKey)!;
    if (timeSinceLastCollision < COLLISION_COOLDOWN) {
      // Ignore collision if not enough time has passed
      console.log("[Damage] Collision ignored - cooldown active:", {
        tank1Id: tank1.playerId,
        tank2Id: tank2.playerId,
        timeSinceLastCollision,
        cooldownRemaining: COLLISION_COOLDOWN - timeSinceLastCollision,
      });
      return;
    }
  }

  // Update last collision time
  lastCollisionTimes.set(collisionKey, currentTime);

  // DEBUG: Log rozpoczęcia obsługi kolizji
  console.log("[Damage] Tank collision handling:", {
    tank1Id: tank1.playerId,
    tank2Id: tank2.playerId,
    tank1Destroyed: tank1.isDestroyed,
    tank2Destroyed: tank2.isDestroyed,
    distance: tank1.mesh.position.distanceTo(tank2.mesh.position),
  });

  // Sprawdź czy któryś z czołgów jest zniszczony, nietykalny lub w strefie respawnu
  //const currentTime = Date.now();
  if (
    tank1.isDestroyed ||
    tank2.isDestroyed ||
    currentTime < tank1.invulnerableUntil ||
    currentTime < tank2.invulnerableUntil ||
    tank1.isInRespawnZone() ||
    tank2.isInRespawnZone()
  ) {
    console.log("[Damage] Collision ignored - tank(s) destroyed, invulnerable or in respawn zone:", {
      tank1: {
        id: tank1.playerId,
        destroyed: tank1.isDestroyed,
        invulnerable: currentTime < tank1.invulnerableUntil,
        inRespawnZone: tank1.isInRespawnZone(),
      },
      tank2: {
        id: tank2.playerId,
        destroyed: tank2.isDestroyed,
        invulnerable: currentTime < tank2.invulnerableUntil,
        inRespawnZone: tank2.isInRespawnZone(),
      },
      currentTime,
    });
    return;
  }

  // Oblicz względną prędkość zderzenia
  const relativeSpeed = Math.max(Math.abs(tank1.currentSpeed), Math.abs(tank2.currentSpeed));

  // Oblicz obrażenia na podstawie prędkości względnej
  const baseDamage = TANK_CONFIG.HEALTH.COLLISION_DAMAGE;
  const speedMultiplier = Math.min(2, relativeSpeed / TANK_CONFIG.MOVEMENT.MAX_SPEED);
  const collisionDamage = Math.max(
    baseDamage * speedMultiplier,
    baseDamage * ENVIRONMENT_CONFIG.COLLISION.COLLISION_DAMAGE_FACTOR
  );

  // DEBUG: Log obliczonych obrażeń
  console.log("[Damage] Collision damage calculated:", {
    tank1Id: tank1.playerId,
    tank2Id: tank2.playerId,
    relativeSpeed,
    baseDamage,
    speedMultiplier,
    finalDamage: collisionDamage,
  });

  // Oblicz punkt kolizji
  const collisionPoint = new THREE.Vector3().addVectors(tank1.mesh.position, tank2.mesh.position).multiplyScalar(0.5);

  // Apply damage locally first for immediate feedback
  applyDamage(tank1, collisionDamage);
  applyDamage(tank2, collisionDamage);

  // Then send to server for validation using playerId
  if (window.socket) {
    window.socket.emit("tankCollision", {
      tank1Id: tank1.playerId,
      tank2Id: tank2.playerId,
      damage: collisionDamage,
      collisionPoint: {
        x: collisionPoint.x,
        y: collisionPoint.y,
        z: collisionPoint.z,
      },
    });

    console.log("[Damage] Sending tank collision to server:", {
      tank1Id: tank1.playerId,
      tank2Id: tank2.playerId,
      damage: collisionDamage,
    });
  }

  // Oblicz wektory odbicia
  const bounce1 = calculateBounceDirection(tank1, tank2);
  const bounce2 = calculateBounceDirection(tank2, tank1);

  // Zastosuj efekt odbicia
  const bounceForce = Math.max(0.5, relativeSpeed * ENVIRONMENT_CONFIG.COLLISION.RANDOM_PUSHBACK_FACTOR);

  // DEBUG: Log efektu odbicia
  console.log("[Damage] Applying bounce effect:", {
    tank1Id: tank1.playerId,
    tank2Id: tank2.playerId,
    bounceForce,
    tank1Bounce: bounce1.toArray(),
    tank2Bounce: bounce2.toArray(),
  });

  // Cofnij czołgi, aby uniknąć "zaklinowania"
  tank1.mesh.position.add(bounce1.multiplyScalar(ENVIRONMENT_CONFIG.COLLISION.COLLISION_BOUNCE_FACTOR));
  tank2.mesh.position.add(bounce2.multiplyScalar(ENVIRONMENT_CONFIG.COLLISION.COLLISION_BOUNCE_FACTOR));

  // Zredukuj prędkości czołgów
  const speedReduction = ENVIRONMENT_CONFIG.COLLISION.SPEED_REDUCTION_ON_COLLISION;
  const oldSpeed1 = tank1.currentSpeed;
  const oldSpeed2 = tank2.currentSpeed;
  tank1.currentSpeed *= -speedReduction;
  tank2.currentSpeed *= -speedReduction;

  // Stwórz mniejszy efekt wybuchu w miejscu kolizji, jeśli mamy dostęp do sceny
  if (tank1.environment?.scene) {
    createExplosion(tank1.environment.scene, collisionPoint);
  }

  // DEBUG: Log końcowego stanu czołgów
  console.log("[Damage] Final tank states after collision:", {
    tank1: {
      id: tank1.playerId,
      oldSpeed: oldSpeed1,
      newSpeed: tank1.currentSpeed,
      health: tank1.health,
      destroyed: tank1.isDestroyed,
      position: tank1.mesh.position.toArray(),
    },
    tank2: {
      id: tank2.playerId,
      oldSpeed: oldSpeed2,
      newSpeed: tank2.currentSpeed,
      health: tank2.health,
      destroyed: tank2.isDestroyed,
      position: tank2.mesh.position.toArray(),
    },
  });
};

export const applyDamage = (tank: Tank, damage: number): void => {
  console.log("[Damage] Starting damage application process:", {
    tankId: tank.playerId,
    damage,
    currentHealth: tank.health,
    maxHealth: tank.maxHealth,
    isDestroyed: tank.isDestroyed,
    invulnerableUntil: tank.invulnerableUntil,
    inRespawnZone: tank.isInRespawnZone(),
  });

  const currentTime = Date.now();
  const isLocalTank = tank === window.playerTank;

  // Sprawdź czy czołg jest nietykalny lub w strefie respawnu
  if (currentTime < tank.invulnerableUntil || tank.isInRespawnZone()) {
    console.log("[Damage] Damage ignored - tank invulnerable or in respawn zone:", {
      tankId: tank.playerId,
      isLocalTank,
      currentTime,
      invulnerableUntil: tank.invulnerableUntil,
      inRespawnZone: tank.isInRespawnZone(),
    });
    return;
  }

  // Sprawdź, czy czołg jest już zniszczony
  if (tank.isDestroyed) {
    console.log(`[Damage] Tank ${tank.playerId} is already destroyed, ignoring damage`);
    return;
  }

  // Zapisz początkowe zdrowie
  const initialHealth = tank.health;

  // Oblicz rzeczywiste obrażenia z uwzględnieniem pancerza i odporności
  const actualDamage = Math.max(0, damage * (1 - tank.damageResistance));
  tank.health = Math.max(0, tank.health - actualDamage);
  tank.lastDamageTime = currentTime;

  console.log("[Damage] Tank damage details:", {
    tankId: tank.playerId,
    initialHealth,
    actualDamage,
    currentHealth: tank.health,
    maxHealth: tank.maxHealth,
    healthPercentage: (tank.health / tank.maxHealth) * 100,
    hasDamageSmoke: tank.damageSmoke,
    damageResistance: tank.damageResistance,
  });

  // Aktywuj efekt dymu przy niskim zdrowiu
  const healthPercentage = (tank.health / tank.maxHealth) * 100;
  const shouldHaveSmoke = healthPercentage < 40;

  console.log("[Damage] Smoke effect evaluation:", {
    tankId: tank.playerId,
    healthPercentage: healthPercentage.toFixed(2),
    shouldHaveSmoke,
    currentlyHasSmoke: tank.damageSmoke,
    healthThreshold: 40,
    tank: tank,
  });

  if (shouldHaveSmoke && !tank.damageSmoke) {
    console.log("[Damage] Creating smoke effect - conditions met:", {
      tankId: tank.playerId,
      healthPercentage: healthPercentage.toFixed(2),
      tankMeshExists: !!tank.mesh,
      tankState: tank,
    });
    //createDamageSmoke(tank); switched off as not working correctly for now
  } else if (!shouldHaveSmoke && tank.damageSmoke) {
    console.log("[Damage] Removing smoke effect - health recovered:", {
      tankId: tank.playerId,
      healthPercentage: healthPercentage.toFixed(2),
    });
    removeDamageSmoke(tank);
  } else {
    console.log("[Damage] No smoke effect change needed:", {
      tankId: tank.playerId,
      healthPercentage: healthPercentage.toFixed(2),
      currentlyHasSmoke: tank.damageSmoke,
      shouldHaveSmoke: shouldHaveSmoke,
    });
  }

  // Sprawdź czy czołg został zniszczony
  if (tank.health <= 0 && !tank.isDestroyed) {
    console.log("[Damage] Tank destroyed - calling destroy():", {
      tankId: tank.playerId,
      finalHealth: tank.health,
    });
    if (tank === window.playerTank) {
      showWastedOverlay();
    }
    tank.destroy();
  }
};

// Update the handleProjectileHit function to ensure damage is properly applied
// sourcePlayerId - ID gracza/bota który wystrzelił pocisk (używane do przypisania killi)
export const handleProjectileHit = (
  projectilePosition: THREE.Vector3,
  hitTank: Tank,
  sourcePlayerId?: string
): void => {
  // Calculate distance from hit to tank center
  const distance = projectilePosition.distanceTo(hitTank.mesh.position);

  // Debug log for hit detection
  console.log("[Client] Projectile hit detection:", {
    tankId: hitTank.playerId,
    distance,
    damageRadius: TANK_CONFIG.SHOOTING.PROJECTILE_DAMAGE_RADIUS,
    position: projectilePosition.toArray(),
    tankPosition: hitTank.mesh.position.toArray(),
    tankHealth: hitTank.health,
    isDestroyed: hitTank.isDestroyed,
    isLocalTank: hitTank === window.playerTank,
  });

  // Sprawdź czy czołg jest w strefie respawnu
  if (hitTank.isInRespawnZone()) {
    //ignore tank in respawn zone
    return;
  }

  // Sprawdź, czy czołg jest już zniszczony
  if (hitTank.isDestroyed) {
    return;
  }

  // If hit is within damage radius
  if (distance <= TANK_CONFIG.SHOOTING.PROJECTILE_DAMAGE_RADIUS) {
    // Calculate damage based on distance (more damage when closer to center)
    const damageMultiplier =
      1 - (distance / TANK_CONFIG.SHOOTING.PROJECTILE_DAMAGE_RADIUS) * TANK_CONFIG.SHOOTING.PROJECTILE_DAMAGE_FALLOFF;
    const damage = TANK_CONFIG.SHOOTING.PROJECTILE_DAMAGE * Math.max(0, damageMultiplier);

    console.log("[Client] Hit detection success:", {
      tankId: hitTank.playerId,
      damage: damage.toFixed(2),
      currentHealth: hitTank.health,
      distance: distance.toFixed(2),
      multiplier: damageMultiplier.toFixed(2),
    });

    // Zawsze wysyłamy damage do serwera - serwer jest źródłem prawdy o HP i respawnach
    const effectiveSourcePlayerId = sourcePlayerId || window.playerTank?.playerId;

    if (window.socket && hitTank.playerId) {
      try {
        console.log("[Damage] Sending damage to server:", {
          targetId: hitTank.playerId,
          sourcePlayerId: effectiveSourcePlayerId,
          damage: damage,
          isLocalTank: hitTank === window.playerTank,
        });
        window.socket.emit("tankDamage", {
          targetId: hitTank.playerId,
          sourcePlayerId: effectiveSourcePlayerId,
          damage: damage,
          projectilePosition: {
            x: projectilePosition.x,
            y: projectilePosition.y,
            z: projectilePosition.z,
          },
        });
      } catch (error) {
        console.error("[Client] Error emitting tankDamage event:", error);
      }
    } else {
      // Fallback - jeśli nie ma socketa, aplikuj lokalnie (nie powinno się zdarzyć)
      console.warn("[Damage] No socket available, applying damage locally");
      applyDamage(hitTank, damage);
    }

    // Create explosion effect
    if (hitTank.environment?.scene) {
      console.log("[Damage] Creating explosion effect for tank:", hitTank.playerId);
      createExplosion(hitTank.environment.scene, projectilePosition); //duplicate explosion?
    }
  }
};

// Add event listeners for damage in your game initialization
// Add this function near the top of the file with other utility functions
export const findTankById = (playerId: string): Tank | undefined => {
  // Najpierw sprawdź czy to nasz czołg
  if (window.playerTank?.playerId === playerId) {
    return window.playerTank;
  }

  // Potem sprawdź w remoteTanks
  const remoteTanks = window.remoteTanks || [];
  const remoteTank = remoteTanks.find((tank) => tank.playerId === playerId);

  if (remoteTank) {
    return remoteTank;
  }

  // Na końcu sprawdź w gameState (jeśli istnieje)
  if (window.gameState?.tanks) {
    const stateTank = window.gameState.tanks.find((tank) => tank.playerId === playerId);
    if (stateTank) {
      return stateTank;
    }
  }

  console.warn("[findTankById] Tank not found:", {
    searchId: playerId,
    availableTanks: window.remoteTanks?.map((t) => ({ id: t.playerId })),
  });
  return undefined;
};

// Update the global interface to include our game state
declare global {
  interface Window {
    socket?: any;
    playerTank?: Tank;
    gameState?: {
      tanks: Tank[];
      scene?: THREE.Scene;
      environmentService?: {
        getRandomRespawnPoint: () => {
          position: THREE.Vector3;
          rotation: number;
        } | null;
      };
      trainingTrackManager?: TrainingTrackManager;
      trainingTrackService?: TrainingTrackService;
      Bot?: typeof Bot;
      botManager?: BotManager;
    };
    tanks?: Tank[];
    remoteTanks?: Tank[];
    showDamageIndicator?: (damage: number) => void;
  }
}

// Add a function to show damage indicator in the HUD
export const showDamageIndicator = (damage: number) => {
  const indicator = document.getElementById("damageIndicator");
  if (indicator) {
    // Pokaż wartość obrażeń
    indicator.textContent = `-${Math.round(damage)}`;
    indicator.style.opacity = "1";

    // Resetuj animację po krótkim czasie
    setTimeout(() => {
      indicator.style.opacity = "0";
    }, 500);
  } else {
    console.warn("DEBUG: Could not find damageIndicator element");
  }
};

// Make the function available globally
window.showDamageIndicator = showDamageIndicator;

if (window.socket) {
  window.socket.on(
    "tankDamage",
    (data: { targetId: string; health: number; damage: number; position: { x: number; y: number; z: number } }) => {
      // Znajdź czołg, który otrzymał obrażenia
      const tank = findTankById(data.targetId);
      if (!tank) {
        console.warn("[Client] Tank not found for damage event:", data.targetId);
        return;
      }

      // Sprawdź, czy czołg jest już zniszczony
      if (tank.isDestroyed) {
        return;
      }

      console.log("Applying damage to tank:", tank.playerId, data.damage);
      // Aplikuj obrażenia używając naszej funkcji
      applyDamage(tank, data.damage);

      // Jeśli stan lokalny różni się znacząco od serwera, synchronizuj
      if (Math.abs(tank.health - data.health) > 0.1) {
        tank.health = data.health;
      }

      // Pokaż efekt trafienia
      if (tank.environment?.scene) {
        const hitPosition = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
        console.log(
          "[Damage] Create explosion for tank:",
          tank.playerId,
          "at",
          hitPosition,
          "with scene",
          tank.environment.scene,
          "inf"
        );
        createExplosion(tank.environment.scene, hitPosition);
      }

      // Jeśli HP spadło do 0, oznacz czołg jako zniszczony
      if (tank.health <= 0) {
        tank.destroy();
      }

      // Aktualizuj HUD jeśli to nasz czołg
      if (tank === window.playerTank && window.updateHUDHealth) {
        window.updateHUDHealth(tank.health);
      }
    }
  );

  // Listener tankRespawn jest obsługiwany w game.ts - tutaj tylko logujemy dla debugowania
  // window.socket.on("tankRespawn") - usunięty żeby uniknąć duplikacji

  // Dodaj obsługę kolizji
  window.socket.on(
    "tankCollision",
    (data: {
      tank1Id: string;
      tank2Id: string;
      damage: number;
      tank1Health: number;
      tank2Health: number;
      collisionPoint: { x: number; y: number; z: number };
    }) => {
      console.log("Received tank collision with full data:", {
        ...data,
        localTankId: window.playerTank?.playerId,
        isLocalTankInvolved:
          window.playerTank &&
          (data.tank1Id === window.playerTank.playerId || data.tank2Id === window.playerTank.playerId),
      });

      // Sprawdź czy dane są poprawne
      if (typeof data.tank1Health === "undefined" || typeof data.tank2Health === "undefined") {
        console.warn("Received collision with undefined health values - ignoring");
        return;
      }

      const tank1 = findTankById(data.tank1Id);
      const tank2 = findTankById(data.tank2Id);

      // Debug info o znalezionych czołgach
      console.log("Found tanks for collision:", {
        tank1Found: !!tank1,
        tank2Found: !!tank2,
        tank1Id: data.tank1Id,
        tank2Id: data.tank2Id,
        tank1Health: tank1?.health,
        tank2Health: tank2?.health,
      });

      // Sprawdź czy oba czołgi istnieją
      if (!tank1 || !tank2) {
        console.warn("Skipping collision - one or both tanks not found");
        return;
      }

      // Sprawdź czy któryś z czołgów nie jest już zniszczony
      if (tank1.isDestroyed || tank2.isDestroyed) {
        console.warn("Skipping collision - one or both tanks are destroyed");
        return;
      }

      // Aktualizuj zdrowie
      const oldHealth1 = tank1.health;
      const oldHealth2 = tank2.health;
      tank1.health = data.tank1Health;
      tank2.health = data.tank2Health;

      console.log("Updated tank health after collision:", {
        tank1: {
          id: tank1.playerId,
          oldHealth: oldHealth1,
          newHealth: data.tank1Health,
        },
        tank2: {
          id: tank2.playerId,
          oldHealth: oldHealth2,
          newHealth: data.tank2Health,
        },
      });

      // Aktualizuj HUD dla lokalnego gracza
      if (window.updateHUDHealth && window.playerTank) {
        if (data.tank1Id === window.playerTank.playerId) {
          window.updateHUDHealth(data.tank1Health);
        } else if (data.tank2Id === window.playerTank.playerId) {
          window.updateHUDHealth(data.tank2Health);
        }
      }

      // Efekt wizualny kolizji
      if (tank1.environment?.scene) {
        createExplosion(
          tank1.environment.scene,
          new THREE.Vector3(data.collisionPoint.x, data.collisionPoint.y, data.collisionPoint.z)
        );
      }

      // Sprawdź zniszczenie czołgów
      if (tank1.health <= 0) tank1.destroy();
      if (tank2.health <= 0) tank2.destroy();
    }
  );
}
